import { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useProductSettings } from '@workspace/utils-react';
import type { Settings } from '../../../AppContainer';
import type { PendingApprovalItem } from '../../../api/shared/types';
import { getPendingApprovals } from '../../../api/approvals';
import { Button } from '../../ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { Badge } from '../../ui/badge';
import { formatDateUK } from '../../../lib/date-utils';

export function NotificationBell() {
  const { apiUrl } = useProductSettings<Settings>();
  const navigate = useNavigate();
  const [approvals, setApprovals] = useState<PendingApprovalItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const fetchApprovals = useCallback(async () => {
    try {
      const data = await getPendingApprovals(apiUrl);
      setApprovals(data);
    } catch (err) {
      console.error('ProjectApproval_ Failed to fetch pending approvals:', err);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  // Re-fetch when an approval or reopen action happens elsewhere (e.g. ReviewPage)
  useEffect(() => {
    const handler = () => {
      fetchApprovals();
    };
    window.addEventListener('approvals-changed', handler);
    return () => window.removeEventListener('approvals-changed', handler);
  }, [fetchApprovals]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await fetchApprovals();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleApprovalClick = (approval: PendingApprovalItem) => {
    setIsOpen(false);
    // Navigate to the review page for this project
    const encodedProject = encodeURIComponent(approval.area_level_1);
    const encodedQuarter = encodeURIComponent(approval.fiscal_year_quarter);
    navigate(`/review/${encodedProject}/${encodedQuarter}/${encodedProject}`);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(!isOpen)}
              className="relative"
            >
              <Bell className="h-5 w-5" />
              {approvals.length > 0 && (
                <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                  {approvals.length > 99 ? '99+' : approvals.length}
                </span>
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Pending Approvals ({approvals.length})</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-96 rounded-lg border bg-background shadow-lg z-50">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <h3 className="font-semibold text-sm">
              Pending Approvals
              {approvals.length > 0 && (
                <Badge variant="secondary" className="ml-2">{approvals.length}</Badge>
              )}
            </h3>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    className="h-8 w-8"
                  >
                    <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Refresh</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {approvals.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                No pending approvals
              </div>
            ) : (
              approvals.map((approval) => (
                <button
                  type="button"
                  key={approval.id}
                  onClick={() => handleApprovalClick(approval)}
                  className="w-full text-left px-4 py-3 hover:bg-muted/50 border-b last:border-b-0 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm truncate">
                      {approval.area_level_1}
                    </span>
                    <Badge variant="outline" className="text-xs ml-2 shrink-0">
                      {approval.fiscal_year_quarter}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                    <span>By {approval.submitted_by}</span>
                    <span>·</span>
                    <span>
                      {formatDateUK(approval.submitted_at)}
                    </span>
                  </div>
                  {approval.comments && (
                    <p className="mt-1 text-xs text-muted-foreground truncate">
                      {approval.comments}
                    </p>
                  )}
                  <div className="flex gap-3 mt-1 text-xs">
                    <span>CapEx: {approval.final_capex_percentage}%</span>
                    <span>R&D: {approval.final_rnd_percentage}%</span>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
